import React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { LinearProgress, Spinner, StatePlaceholder } from "@omega/ui";
import { EmptyState } from "../empty-state";
import "./PickerList.scss";

/** Дефолтная оценка высоты строки {@link PickerList}. */
const DEFAULT_PICKER_LIST_ITEM_HEIGHT = 48;

/** Дефолтное количество дополнительных строк вне viewport. */
const DEFAULT_PICKER_LIST_OVERSCAN = 6;

/** Ключ строки {@link PickerList}. */
export type PickerListItemKey = string | number;

/** Настройки виртуализации {@link PickerList}. */
export interface PickerListVirtualizationConfig {
    /** Включает virtualized-рендер строк. @default true */
    enabled?: boolean;
    /** Оценочная высота строки в пикселях. @default 48 */
    estimateItemHeight?: number;
    /** Количество дополнительных строк за пределами viewport. @default 6 */
    overscan?: number;
}

/** Пропсы {@link PickerList}. */
export interface PickerListProps<T> {
    /** Элементы списка; `null` — загрузка ещё не началась. */
    items: T[] | null;
    /**
     * Первичная / blocking-загрузка. \
     * Заменяет список на placeholder. Не путать с {@link isRefreshing}.
     */
    isLoading?: boolean;
    /**
     * Фоновое обновление уже показанного списка. \
     * Список остаётся видимым; сверху `LinearProgress`.
     */
    isRefreshing?: boolean;
    /**
     * Проверяет, выбрана ли строка.
     * @param item элемент списка
     */
    isSelected: (item: T) => boolean;
    /**
     * Рендерит содержимое строки.
     * @param item элемент списка
     */
    renderItem: (item: T) => React.ReactNode;
    /**
     * Вызывается при выборе строки.
     * @param item выбранный элемент
     */
    onSelect: (item: T) => void;
    /** Текст пустого списка. @default "Нет результатов" */
    emptyText?: string;
    /** Содержимое до начала поиска (когда `items === null`). @default "Введите запрос для поиска" */
    idleText?: React.ReactNode;
    /**
     * Возвращает стабильный ключ строки. \
     * Для больших списков лучше передавать доменный `id`, иначе используется индекс.
     * @param item элемент списка
     * @param index индекс элемента
     */
    getItemKey?: (item: T, index: number) => PickerListItemKey;
    /**
     * Управляет virtualized-рендером списка.
     * @default true
     */
    virtualization?: boolean | PickerListVirtualizationConfig;
    /** Дополнительный CSS-класс. */
    className?: string;
}

function resolvePickerListVirtualizationConfig(
    virtualization: boolean | PickerListVirtualizationConfig | undefined,
): Required<PickerListVirtualizationConfig> {
    if (virtualization === false) {
        return {
            enabled: false,
            estimateItemHeight: DEFAULT_PICKER_LIST_ITEM_HEIGHT,
            overscan: 0,
        };
    }

    if (virtualization === true || virtualization == null) {
        return {
            enabled: true,
            estimateItemHeight: DEFAULT_PICKER_LIST_ITEM_HEIGHT,
            overscan: DEFAULT_PICKER_LIST_OVERSCAN,
        };
    }

    return {
        enabled: virtualization.enabled !== false,
        estimateItemHeight: virtualization.estimateItemHeight ?? DEFAULT_PICKER_LIST_ITEM_HEIGHT,
        overscan: virtualization.overscan ?? DEFAULT_PICKER_LIST_OVERSCAN,
    };
}

function canMeasurePickerListItem(element: HTMLDivElement | null): element is HTMLDivElement {
    return element != null && element.offsetParent != null;
}

/**
 * Touch-friendly список выбора для picker window и SearchSelect. \
 * Blocking load → `StatePlaceholder` + `Spinner`; soft refetch → `LinearProgress` поверх списка.
 * @template T тип элемента списка
 */
export function PickerList<T>({
    items,
    isLoading = false,
    isRefreshing = false,
    isSelected,
    renderItem,
    onSelect,
    emptyText = "Нет результатов",
    idleText = "Введите запрос для поиска",
    getItemKey,
    virtualization,
    className,
}: PickerListProps<T>) {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const virtualizationConfig = resolvePickerListVirtualizationConfig(virtualization);
    const rowVirtualizer = useVirtualizer({
        count: items?.length ?? 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => virtualizationConfig.estimateItemHeight,
        overscan: virtualizationConfig.overscan,
        enabled: virtualizationConfig.enabled && items != null && items.length > 0,
        getItemKey: (index) => {
            const item = items?.[index];
            if (item == null) return index;
            return getItemKey?.(item, index) ?? index;
        },
    });

    if (isLoading === true) {
        return (
            <div className={classNames("shared_picker_list", "shared_picker_list--center", className)} role="status">
                <StatePlaceholder size="md" title="Загрузка..." icon={<Spinner size="md" />} />
            </div>
        );
    }

    if (items == null) {
        return <EmptyState text={idleText} className={className} />;
    }

    if (items.length === 0) {
        return <EmptyState text={emptyText} className={className} />;
    }

    const showRefreshIndicator = isRefreshing === true;

    if (virtualizationConfig.enabled) {
        return (
            <div
                ref={scrollRef}
                className={classNames("shared_picker_list", "shared_picker_list--with_refresh", className)}
                role="listbox"
                aria-busy={showRefreshIndicator || undefined}
            >
                {showRefreshIndicator ? (
                    <LinearProgress aria-label="Обновление списка" className="shared_picker_list__refresh" />
                ) : null}
                <div className="shared_picker_list__virtual_spacer" style={{ height: rowVirtualizer.getTotalSize() }}>
                    {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                        const item = items[virtualItem.index];
                        if (item == null) return null;

                        const selected = isSelected(item);
                        return (
                            <div
                                key={virtualItem.key}
                                ref={(element) => {
                                    if (!canMeasurePickerListItem(element)) return;
                                    rowVirtualizer.measureElement(element);
                                }}
                                data-index={virtualItem.index}
                                role="option"
                                aria-selected={selected}
                                className="shared_picker_list__virtual_item"
                                style={{ transform: `translateY(${virtualItem.start}px)` }}
                            >
                                <button
                                    type="button"
                                    className={classNames("shared_picker_list__item", {
                                        "shared_picker_list__item--selected": selected,
                                    })}
                                    onClick={() => onSelect(item)}
                                >
                                    {renderItem(item)}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div
            className={classNames("shared_picker_list", "shared_picker_list--with_refresh", className)}
            aria-busy={showRefreshIndicator || undefined}
        >
            {showRefreshIndicator ? (
                <LinearProgress aria-label="Обновление списка" className="shared_picker_list__refresh" />
            ) : null}
            <ul className="shared_picker_list__items" role="listbox">
                {items.map((item, index) => {
                    const selected = isSelected(item);
                    return (
                        <li key={index} role="option" aria-selected={selected}>
                            <button
                                type="button"
                                className={classNames("shared_picker_list__item", {
                                    "shared_picker_list__item--selected": selected,
                                })}
                                onClick={() => onSelect(item)}
                            >
                                {renderItem(item)}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
